"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.refreshPanelsOrRollbackTransform = exports.rollbackObjectsTransform = exports.refreshPlasticPanelsOrRollback = exports.refreshPlasticPanels = exports.applyPlasticPanelCutouts = exports.planCutoutsForPanel = exports.getPersistedPanelCutouts = exports.getPersistedPanelCutContour = void 0;
const three_1 = require("three");
const types_1 = require("~/store/types");
const events_1 = require("../../../utils/events");
const consts_1 = require("../consts");
const PanelCutoutPlanner_3d_1 = require("./PanelCutoutPlanner.3d");
const PanelInSlotsConsts_1 = require("./PanelInSlots/PanelInSlotsConsts");
const utils_1 = require("../utils");
const tolerance = 1e-5;
function getPersistedPanelCutContour(panel) {
    return panel.autoCutouts ? panel.cutContour.map((point) => point.clone().multiply(panel.scale)) : panel.cutContour;
}
exports.getPersistedPanelCutContour = getPersistedPanelCutContour;
function getPersistedPanelCutouts(panel) {
    var _a, _b;
    return {
        outerContour: getPersistedPanelCutContour(panel),
        holeContours: ((_a = panel.holeContours) !== null && _a !== void 0 ? _a : []).map((hole) => hole.map((point) => point.clone().multiply(panel.scale))),
        autoCutouts: Boolean(panel.autoCutouts),
        notchesCount: (_b = panel.notchesCount) !== null && _b !== void 0 ? _b : 0,
    };
}
exports.getPersistedPanelCutouts = getPersistedPanelCutouts;
function convexHull(points) {
    // ShapeUtils has no convex-hull helper. Keep the tolerance here: profiles often
    // contain near-collinear tessellation points that must not become cutout corners.
    const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    const lower = [];
    const upper = [];
    sorted.forEach((point) => {
        while (lower.length > 1 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= tolerance) {
            lower.pop();
        }
        lower.push(point);
    });
    sorted.reverse().forEach((point) => {
        while (upper.length > 1 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= tolerance) {
            upper.pop();
        }
        upper.push(point);
    });
    return lower.slice(0, -1).concat(upper.slice(0, -1));
}
function getExtrusionLocalPoints(extrusion, size) {
    var _a;
    const position = (_a = extrusion.mesh) === null || _a === void 0 ? void 0 : _a.geometry.attributes.position;
    if (position) {
        return Array.from({ length: position.count }, (_, index) => new three_1.Vector3().fromBufferAttribute(position, index));
    }
    const bounds = new three_1.Box3(new three_1.Vector3(-size.x / 2, -size.y / 2, -size.z), new three_1.Vector3(size.x / 2, size.y / 2, 0));
    return [
        new three_1.Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
        new three_1.Vector3(bounds.min.x, bounds.min.y, bounds.max.z),
        new three_1.Vector3(bounds.min.x, bounds.max.y, bounds.min.z),
        new three_1.Vector3(bounds.min.x, bounds.max.y, bounds.max.z),
        new three_1.Vector3(bounds.max.x, bounds.min.y, bounds.min.z),
        new three_1.Vector3(bounds.max.x, bounds.min.y, bounds.max.z),
        new three_1.Vector3(bounds.max.x, bounds.max.y, bounds.min.z),
        new three_1.Vector3(bounds.max.x, bounds.max.y, bounds.max.z),
    ];
}
function planCutoutsForPanel(panel, extrusions) {
    const blankSize = panel.partMetadata.boundingBoxSize;
    if (!blankSize) {
        throw new Error('Plastic Panel blank dimensions are unavailable.');
    }
    panel.updateMatrixWorld(true);
    const origin = panel.localToWorld(new three_1.Vector3());
    const xAxis = new three_1.Vector3(1, 0, 0).transformDirection(panel.matrixWorld);
    const yAxis = new three_1.Vector3(0, 1, 0).transformDirection(panel.matrixWorld);
    const normal = new three_1.Vector3(0, 0, -1).transformDirection(panel.matrixWorld);
    const width = blankSize.x * panel.scale.x;
    const height = blankSize.y * panel.scale.y;
    const thickness = blankSize.z * panel.scale.z;
    const footprints = [];
    extrusions.forEach((extrusion) => {
        const size = extrusion.partMetadata.boundingBoxSize;
        if (!size) {
            throw new Error(`Extrusion ${extrusion.uuid} has no usable profile dimensions.`);
        }
        extrusion.updateMatrixWorld(true);
        const localPoints = getExtrusionLocalPoints(extrusion, size);
        const worldPoints = localPoints.map((point) => point.applyMatrix4(extrusion.matrixWorld).sub(origin));
        const depths = worldPoints.map((point) => point.dot(normal));
        if (Math.max(...depths) <= tolerance || Math.min(...depths) >= thickness - tolerance) {
            return;
        }
        const footprint = convexHull(worldPoints.map((point) => new three_1.Vector2(point.dot(xAxis), point.dot(yAxis))));
        if (footprint.length < 3) {
            throw new Error(`Extrusion ${extrusion.uuid} has no valid panel-crossing profile.`);
        }
        const footprintBounds = new three_1.Box3().setFromPoints(footprint.map((point) => new three_1.Vector3(point.x, point.y, 0)));
        if (footprintBounds.max.x <= -width + tolerance ||
            footprintBounds.min.x >= -tolerance ||
            footprintBounds.max.y <= -height + tolerance ||
            footprintBounds.min.y >= -tolerance) {
            return;
        }
        footprints.push({ id: extrusion.uuid, footprint });
    });
    const plan = (0, PanelCutoutPlanner_3d_1.planPlasticPanelCutouts)({
        panelContour: [new three_1.Vector2(-width, -height), new three_1.Vector2(0, -height), new three_1.Vector2(0, 0), new three_1.Vector2(-width, 0)],
        extrusions: footprints,
        clearance: PanelInSlotsConsts_1.notchDistance,
    });
    const toLocal = (point) => new three_1.Vector3(point.x / panel.scale.x, point.y / panel.scale.y, 0);
    return {
        outerContour: plan.outerContour.map(toLocal),
        holeContours: plan.holeContours.map((hole) => hole.map(toLocal)),
        notchesCount: plan.notchesCount,
    };
}
exports.planCutoutsForPanel = planCutoutsForPanel;
function applyPlasticPanelCutouts(panel, extrusions) {
    const plan = planCutoutsForPanel(panel, extrusions);
    if (!plan.notchesCount) {
        if (panel.autoCutouts) {
            panel.restoreStandardGeometry();
        }
        return;
    }
    panel.applyExtrudeGeometry(plan.outerContour, consts_1.consts.DP_UP_DIR, plan.holeContours);
    // eslint-disable-next-line no-param-reassign
    panel.notchesCount = plan.notchesCount;
    // eslint-disable-next-line no-param-reassign
    panel.autoCutouts = true;
    panel.updateMatrixWorld(true);
}
exports.applyPlasticPanelCutouts = applyPlasticPanelCutouts;
function refreshPlasticPanels(viewer, appliedFromHistory = false) {
    const sceneObjects = viewer.getScene().children;
    const extrusions = sceneObjects.filter((object) => object.type === 'Extrusion');
    const updates = [];
    let isValid = true;
    sceneObjects.forEach((object) => {
        var _a, _b;
        if (object.type !== 'Panel') {
            return;
        }
        const panel = object;
        if (!utils_1.Utils.isPlasticPanel(panel) || panel.isWireMesh() || (panel.cutContour.length && !panel.autoCutouts)) {
            return;
        }
        const wasCut = panel.autoCutouts;
        try {
            applyPlasticPanelCutouts(panel, extrusions);
            delete panel.userData.cutoutValidationError;
        }
        catch (error) {
            isValid = false;
            panel.restoreStandardGeometry();
            panel.notchesCount = 0;
            const message = error instanceof Error ? error.message : 'Plastic Panel cutout geometry is invalid.';
            panel.userData.cutoutValidationError = message;
            panel.showErrorBox();
            document.body.dispatchEvent((0, events_1.dispatchNotificationEvent)({ message, type: 'error' }));
        }
        if (!wasCut && !panel.autoCutouts && !panel.userData.cutoutValidationError) {
            return;
        }
        viewer.collisionProcessor.notifyObjectUpdated(panel, true);
        updates.push({
            actionType: types_1.ACTION_TYPE.SCALE2D,
            size: panel.getLength(),
            itemNumber: panel.partDetails.itemNumber,
            itemType: panel.type,
            uuid: panel.uuid,
            position: (_a = panel.originalPosition) !== null && _a !== void 0 ? _a : panel.position,
            geomkernelPosition: panel.position,
            panelSize: (_b = panel.originalPanelSize) !== null && _b !== void 0 ? _b : panel.getSize(),
            geomkernelPanelSize: panel.getSize(),
            panelCutoutGeometry: getPersistedPanelCutouts(panel),
            rotation: panel.quaternion,
            appliedFromHistory,
        });
    });
    if (!isValid) {
        return false;
    }
    if (updates.length) {
        document.body.dispatchEvent((0, events_1.updateItemsToBOMEvent)(updates));
    }
    return true;
}
exports.refreshPlasticPanels = refreshPlasticPanels;
/** Refresh dependent Plastic Panels, restoring the initiating mutation if it is invalid. */
function refreshPlasticPanelsOrRollback(viewer, appliedFromHistory, rollback) {
    if (refreshPlasticPanels(viewer, appliedFromHistory)) {
        return true;
    }
    rollback();
    refreshPlasticPanels(viewer, appliedFromHistory);
    return false;
}
exports.refreshPlasticPanelsOrRollback = refreshPlasticPanelsOrRollback;
/** Helper to restore object states, update matrices, and notify connections/collisions upon mutation rollback. */
function rollbackObjectsTransform(viewer, objects, restore) {
    restore();
    objects.forEach((object) => {
        object.updateMatrixWorld(true);
        viewer.collisionProcessor.notifyObjectUpdated(object);
    });
    viewer.getDesignProMain().updateConnectionsAndShowNotifications(...objects);
}
exports.rollbackObjectsTransform = rollbackObjectsTransform;
/** Helper to refresh dependent panels, rolling back objects transform and notifying collisions/connections if invalid. */
function refreshPanelsOrRollbackTransform(viewer, appliedFromHistory, objects, restore) {
    return refreshPlasticPanelsOrRollback(viewer, appliedFromHistory, () => {
        rollbackObjectsTransform(viewer, objects, restore);
    });
}
exports.refreshPanelsOrRollbackTransform = refreshPanelsOrRollbackTransform;
